import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { LiveRoom } from './api';
import { oneliveAssets } from './assets';
import { handleLiveRoomClick } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';

interface OneliveFeaturedSliderProps {
  rooms: LiveRoom[];
}

function loopOffset(index: number, active: number, count: number): number {
  let offset = index - active;
  if (count <= 1) return 0;
  if (offset > count / 2) offset -= count;
  if (offset < -count / 2) offset += count;
  return offset;
}

export default function OneliveFeaturedSlider({ rooms }: OneliveFeaturedSliderProps) {
  const items = useMemo(() => {
    const featured = rooms.filter((r) => r.is_featured && r.status === 'live');
    return featured.length > 0 ? featured : rooms.filter((r) => r.status === 'live').slice(0, 10);
  }, [rooms]);

  const liveUiMessages = useLiveUiMessages();
  const [active, setActive] = useState(0);
  const count = items.length;

  const go = useCallback((dir: -1 | 1) => {
    if (count <= 1) return;
    setActive((i) => (i + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = window.setInterval(() => go(1), 8000);
    return () => window.clearInterval(timer);
  }, [count, go]);

  if (items.length === 0) return null;

  const handleSlideClick = (index: number, offset: number) => {
    if (offset === 0) {
      handleLiveRoomClick(items[index], liveUiMessages, '/');
      return;
    }
    setActive(index);
  };

  return (
    <section className="onelive-featured-slider">
      <div className="onelive-featured-slider-head">
        <h2 className="onelive-section-title">추천 LIVE</h2>
      </div>
      <div className="onelive-featured-coverflow-wrap">
        <button type="button" className="onelive-featured-nav prev" onClick={() => go(-1)} aria-label="이전">
          <img src={oneliveAssets.arrowLeftBlack} alt="" />
        </button>

        <div className="onelive-featured-coverflow-stage">
          {items.map((room, index) => {
            const offset = loopOffset(index, active, count);
            if (Math.abs(offset) > 3) return null;

            const slideStyle = { '--slide-offset': offset } as CSSProperties;

            return (
              <article
                key={room.uuid}
                className={`onelive-featured-coverflow-slide${offset === 0 ? ' is-active' : ''}`}
                style={slideStyle}
                data-offset={offset}
                onClick={() => handleSlideClick(index, offset)}
                role="button"
                tabIndex={offset === 0 ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSlideClick(index, offset);
                }}
              >
                <div className="onelive-featured-thumb">
                  {room.thumbnail ? (
                    <img
                      src={room.thumbnail}
                      alt=""
                      loading={offset === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  ) : (
                    <span className="onelive-thumb-placeholder">{room.bj_nickname?.[0]}</span>
                  )}
                  {offset === 0 ? <span className="onelive-live-badge">LIVE</span> : null}
                  <div className="onelive-featured-about">
                    <span className="onelive-featured-avatar">
                      {room.avatar ? <img src={room.avatar} alt="" /> : room.bj_nickname?.[0]}
                    </span>
                    <div className="onelive-featured-about-text">
                      <strong>{room.title}</strong>
                      <p>{room.bj_nickname}</p>
                    </div>
                  </div>
                  {offset !== 0 ? <span className="onelive-featured-side-veil" aria-hidden /> : null}
                </div>
              </article>
            );
          })}
        </div>

        <button type="button" className="onelive-featured-nav next" onClick={() => go(1)} aria-label="다음">
          <img src={oneliveAssets.arrowRightBlack} alt="" />
        </button>
      </div>
    </section>
  );
}
